import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchDocumentCenterServices, fetchDocumentTemplateTags, fetchLawyers, fetchProvinces, fetchServiceCategories, type SelectOption } from '../data/referenceData';
import { useAuth } from './AuthContext';
interface ReferenceDataContextType {
  provinces: SelectOption[];
  serviceCategories: SelectOption[];
  lawyers: SelectOption[];
  documentTags: SelectOption[];
  documentServices: SelectOption[];
  isLoading: boolean;
  loadError: string | null;
  loadWarnings: string[];
  refresh: () => Promise<void>;
}
const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);
type LoaderResult = {
  key: string;
  options: SelectOption[];
  error?: string;
};
async function loadWithLabel(key: string, loader: () => Promise<SelectOption[]>): Promise<LoaderResult> {
  try {
    const options = await loader();
    return {
      key,
      options: Array.isArray(options) ? options : []
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return {
      key,
      options: [],
      error: `${key}: ${message}`
    };
  }
}
export function ReferenceDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated
  } = useAuth();
  const [provinces, setProvinces] = useState<SelectOption[]>([]);
  const [serviceCategories, setServiceCategories] = useState<SelectOption[]>([]);
  const [lawyers, setLawyers] = useState<SelectOption[]>([]);
  const [documentTags, setDocumentTags] = useState<SelectOption[]>([]);
  const [documentServices, setDocumentServices] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadWarnings, setLoadWarnings] = useState<string[]>([]);
  const refresh = async () => {
    setIsLoading(true);
    setLoadError(null);
    setLoadWarnings([]);
    const results = await Promise.all([loadWithLabel('provinces', fetchProvinces), loadWithLabel('practice areas', fetchServiceCategories), loadWithLabel('lawyers', fetchLawyers), loadWithLabel('document templates', fetchDocumentTemplateTags), loadWithLabel('document services', fetchDocumentCenterServices)]);
    const byKey = Object.fromEntries(results.map(r => [r.key, r]));
    setProvinces(byKey.provinces?.options ?? []);
    setServiceCategories(byKey['practice areas']?.options ?? []);
    setLawyers(byKey.lawyers?.options ?? []);
    setDocumentTags(byKey['document templates']?.options ?? []);
    setDocumentServices(byKey['document services']?.options ?? []);
    const warnings = results.filter(r => r.error).map(r => r.error as string);
    setLoadWarnings(warnings);
    const critical = ['provinces', 'practice areas', 'lawyers'];
    const criticalFailures = results.filter(r => r.error && critical.includes(r.key));
    if (criticalFailures.length === critical.length) {
      setLoadError(criticalFailures[0]?.error ?? 'Failed to load reference data');
    } else if (criticalFailures.length > 0) {
      setLoadWarnings(prev => [...prev, `Some lists could not be loaded: ${criticalFailures.map(r => r.key).join(', ')}`]);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    }
  }, [isAuthenticated]);
  return <ReferenceDataContext.Provider value={{
    provinces,
    serviceCategories,
    lawyers,
    documentTags,
    documentServices,
    isLoading,
    loadError,
    loadWarnings,
    refresh
  }} data-id="element-315">
      {children}
    </ReferenceDataContext.Provider>;
}
export function useReferenceData() {
  const context = useContext(ReferenceDataContext);
  if (!context) {
    throw new Error('useReferenceData must be used within ReferenceDataProvider');
  }
  return context;
}